import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { IMAGE_URL } from "../../path";
import { getInternationalBrand } from "../../actions/ApiCalls";

interface Product {
    id: number;
    name: string;
    image: string;
    slug: string;
    description?: string;
}

function InternationalBrand() {
    const { brand } = useParams<{ brand?: string }>();
    const [brandData, setBrandData] = useState<any>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [imageRatios, setImageRatios] = useState<{ [key: number]: number }>({});
    const navigate = useNavigate();

    const wineURL = IMAGE_URL;
    const customer_code = localStorage.getItem("customer_code");

    useEffect(() => {
        if (brand) {
            getInternationalBrand(brand)
                .then((response) => {

                    setBrandData(response.data.data);
                })
                .catch((err) =>
                    console.error("Failed to fetch international brand:", err)
                );
        }
    }, [brand]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const isDesktop = windowWidth > 835;

    const handleImageClick = (slug: string) => {
        navigate(`/our-wines/international/${brand}/${slug}`);
    };

    const handleViewPortfolioClick = () => {
        const productsSection = document.getElementById("products");
        if (productsSection) {
            const offsetTop = productsSection.offsetTop - 100;
            window.scrollTo({ top: offsetTop, behavior: "smooth" });
        }
    };

    const calculateImageDimensions = (imageRatio: number, containerHeight: number) => {
        const maxHeight = containerHeight - 100;
        const maxWidth = "90%";
        if (imageRatio > 1.5) {
            return { height: `${maxHeight}px`, width: "auto", maxWidth };
        } else {
            return {
                height: "auto",
                width: maxWidth,
                maxHeight: `${containerHeight < 500 ? containerHeight * 0.6 : containerHeight * 0.7}px`,
            };
        }
    };

    if (!brandData) return null;

    const products: Product[] = brandData.products || [];

    const getDesktopLayout = () =>
        products.map((product) => {
            const boxWidth = window.innerWidth * 0.25 - 25;
            return (
                <div
                    key={product.id}
                    style={{
                        flex: `0 0 ${boxWidth}px`,
                        textAlign: "center",
                        paddingBottom: "10px",
                        maxHeight: "750px",
                        width: `${boxWidth}px`,
                        cursor: "pointer",
                    }}
                    onClick={() => handleImageClick(product.slug)}
                >
                    <div
                        style={{
                            width: "100%",
                            backgroundColor: "#f5f5f5",
                            height: "600px",
                            position: "relative",
                            padding: "0 30px",
                            boxSizing: "border-box",
                        }}
                    >
                        <img
                            src={wineURL + product.image}
                            alt={product.name}
                            onLoad={(e) => {
                                const img = e.target as HTMLImageElement;
                                const ratio = img.naturalHeight / img.naturalWidth;
                                setImageRatios((prev) => ({ ...prev, [product.id]: ratio }));
                            }}
                            style={{
                                position: "absolute",
                                bottom: "50px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                ...calculateImageDimensions(imageRatios[product.id] || 1.5, 600),
                                objectFit: "contain",
                            }}
                        />
                    </div>
                    <p style={textLight}>{brandData.name}</p>
                    <p style={productName}>{product.name}</p>
                </div>
            );
        });

    const getMobileLayout = () =>
        products.map((product) => (
            <div
                key={product.id}
                style={{
                    width: "100%",
                    textAlign: "center",
                    paddingBottom: "30px",
                    maxHeight: "750px",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        backgroundColor: "#f5f5f5",
                        height: "400px",
                        position: "relative",
                        padding: "0 30px",
                        boxSizing: "border-box",
                    }}
                    onClick={() => handleImageClick(product.slug)}
                >
                    <img
                        src={wineURL + product.image}
                        alt={product.name}
                        onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            const ratio = img.naturalHeight / img.naturalWidth;
                            setImageRatios((prev) => ({ ...prev, [product.id]: ratio }));
                        }}
                        style={{
                            position: "absolute",
                            bottom: "50px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            ...calculateImageDimensions(imageRatios[product.id] || 1.5, 400),
                            objectFit: "contain",
                        }}
                    />
                </div>
                <p style={textLight}>{brandData.name}</p>
                <p style={productName}>{product.name}</p>
            </div>
        ));

    return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", width: "100%" }}>
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: "#fff" }}>
                <Header />
            </div>

            <main
                style={{
                    flex: "1",
                    paddingTop: windowWidth <= 575 ? "70px" : "90px",
                    paddingBottom: "80px",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",

                }}
            >
                <div style={{ maxWidth: "100%", margin: "0 auto", width: "100%" }}>
                    {/* Brand Info Section */}
                    <div style={{display: "flex"}}>
                        <div style={{flex: 1}}></div>
                        <div style={{flex: 1}}>
                            <div
                                style={{
                                    padding: "24px",
                                    textAlign: "left",
                                }}
                            >
                                <img
                                    src={wineURL + brandData.bw_logo}
                                    alt={brandData.name}
                                    style={{ width: "150px", marginBottom: "24px" }}
                                />
                                <div style={{width: '65%'}}>
                                    <p style={{ ...textLight }} dangerouslySetInnerHTML={{ __html: brandData.content }} />
                                </div>
                                <div style={{ display: "flex", gap: "10px", marginTop: "20px", flexWrap: "wrap", marginBottom: '72px' }}>
                                    {brandData.instagram_url && (
                                        <a style={linkStyle} href={brandData.instagram_url} target="_blank" rel="noreferrer">
                                            INSTAGRAM
                                        </a>
                                    )}
                                    {brandData.facebook_url && (
                                        <a style={linkStyle} href={brandData.facebook_url} target="_blank" rel="noreferrer">
                                            FACEBOOK
                                        </a>
                                    )}
                                    {brandData.twitter_url && (
                                        <a style={linkStyle} href={brandData.twitter_url} target="_blank" rel="noreferrer">
                                            X.COM
                                        </a>
                                    )}
                                    {brandData.website_url && (
                                        <a style={linkStyle} href={brandData.website_url} target="_blank" rel="noreferrer">
                                            WEBSITE
                                        </a>
                                    )}
                                    <a style={{ ...linkStyle, cursor: "pointer", textDecoration: "underline" }} onClick={handleViewPortfolioClick}>
                                        VIEW PORTFOLIO
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Hero Image */}
                    {brandData.cover_image && (
                        <div style={{ width: "100%", paddingBottom: "40px" }}>
                            <img
                                src={wineURL + brandData.cover_image}
                                alt={brandData.name}
                                style={{ width: "100%", display: "block", verticalAlign: "middle" }}
                            />
                        </div>
                    )}

                    {/* Products Grid */}
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <div
                            id="products"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "flex-start",
                                gap: "40px 20px",
                                padding: "30px 20px",
                                width: "100%",
                            }}
                        >
                            {isDesktop ? getDesktopLayout() : getMobileLayout()}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

// Styles
const textLight: React.CSSProperties = {
    fontWeight: 400,
    marginBottom: 0,
    fontSize: "13px",
    lineHeight: "16.9px",
};

const productName: React.CSSProperties = {
    fontWeight: 600,
    textTransform: "uppercase",
    marginTop: 0,
    marginBottom: 0,
    fontSize: "13px",
    lineHeight: "16.9px",
};

const linkStyle: React.CSSProperties = {
    color: "black",
    fontSize: "10px",
    fontFamily: "'Vinimark-Diatype', sans-serif",
    fontWeight: 600,
};

export default InternationalBrand;
